import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ColorSchemeDto, ColorCriterion } from "../components/workShiftPlanning/utils/ShiftColors";
import {
    getColorScheme,
    saveColorScheme,
    resetColorScheme
} from "../services/colorSchemeService";

interface ColorSchemeState {
    colorSchemes: Record<string, ColorSchemeDto>;
    loading: Record<string, boolean>;
    saving: Record<string, boolean>;
    error: string | null;
}

const initialColorScheme: ColorSchemeDto = {
    id: null,
    rowId: null,
    scope: "workShiftPlanning",
    enabled: false,
    criterion: ColorCriterion.Employee,
    defaultColorKey: "blue",
    mappings: {},
};

const initialState: ColorSchemeState = {
    colorSchemes: {},
    loading: {},
    saving: {},
    error: null,
};

export const fetchColorScheme = createAsyncThunk(
    "colorScheme/fetch",
    async(scope: string = "workShiftPlanning", { signal }) => {
        const result = await getColorScheme(scope, signal);
        return { scope, colorScheme: result };
    }
);

export const persistColorScheme = createAsyncThunk(
    "colorScheme/persist",
    async(dto: ColorSchemeDto) => {
        const result = await saveColorScheme(dto);
        return { scope: dto.scope, colorScheme: result };
    }
);

export const clearColorScheme = createAsyncThunk(
    "colorScheme/reset",
    async(scope: string = "workShiftPlanning") => {
        await resetColorScheme(scope);
        return { scope, colorScheme: initialColorScheme };
    }
);

export const colorSchemeSlice = createSlice({
    name: "colorScheme",
    initialState,
    reducers: {
        setColorSchemeLocal: (state, action: PayloadAction<ColorSchemeDto>) => {
            state.colorSchemes[action.payload.scope] = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchColorScheme.pending, (state, action) => {
            const scope = action.meta.arg;
            state.loading[scope] = true;
            state.error = null;
        });
        builder.addCase(fetchColorScheme.fulfilled, (state, action) => {
            const { scope, colorScheme } = action.payload;
            state.loading[scope] = false;
            state.colorSchemes[scope] = colorScheme ?? { ...initialColorScheme, scope };
        });
        builder.addCase(fetchColorScheme.rejected, (state, action) => {
            const scope = action.meta.arg;
            state.loading[scope] = false;
            state.error = action.error.message ?? "Failed to fetch color scheme";
        });

        builder.addCase(persistColorScheme.pending, (state, action) => {
            const scope = action.meta.arg.scope;
            state.saving[scope] = true;
            state.error = null;
        });
        builder.addCase(persistColorScheme.fulfilled, (state, action) => {
            const { scope, colorScheme } = action.payload;
            state.saving[scope] = false;
            state.colorSchemes[scope] = colorScheme ?? { ...initialColorScheme, scope };
        });
        builder.addCase(persistColorScheme.rejected, (state, action) => {
            const scope = action.meta.arg.scope;
            state.saving[scope] = false;
            state.error = action.error.message ?? "Failed to save color scheme";
        });

        builder.addCase(clearColorScheme.pending, (state, action) => {
            const scope = action.meta.arg;
            state.saving[scope] = true;
            state.error = null;
        });
        builder.addCase(clearColorScheme.fulfilled, (state, action) => {
            const { scope, colorScheme } = action.payload;
            state.saving[scope] = false;
            state.colorSchemes[scope] = colorScheme;
        });
        builder.addCase(clearColorScheme.rejected, (state, action) => {
            const scope = action.meta.arg;
            state.saving[scope] = false;
            state.error = action.error.message ?? "Failed to reset color scheme";
        });
    },
});

export const { setColorSchemeLocal, clearError } = colorSchemeSlice.actions;
export const colorSchemeReducer = colorSchemeSlice.reducer;

export const selectColorSchemeByScope = (scope: string = "workShiftPlanning") =>
    (state: { colorScheme: ColorSchemeState }) =>
        state.colorScheme.colorSchemes[scope] ?? { ...initialColorScheme, scope };

export const selectColorSchemeLoadingByScope = (scope: string = "workShiftPlanning") =>
    (state: { colorScheme: ColorSchemeState }) =>
        state.colorScheme.loading[scope] ?? false;

export const selectColorSchemeSavingByScope = (scope: string = "workShiftPlanning") =>
    (state: { colorScheme: ColorSchemeState }) =>
        state.colorScheme.saving[scope] ?? false;

export const selectColorSchemeError = (state: { colorScheme: ColorSchemeState }) =>
    state.colorScheme.error;